/* ============================================================================
   CART REFRESH AFTER A 3D BUILD IS ADDED

   The builder adds to the cart with a plain fetch to /cart/add.js and then
   announces it with:

       document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))

   Nothing in this theme listens for that name. Its own event, from
   assets/events.js, is 'cart:update', and cart-drawer.js reads
   event.detail.resource.item_count off it. So the item really was added and
   the shopper was told 'Added!', while the header and the drawer carried on
   showing the old cart.

   WHERE THE DELAY WENT
   The first version fixed the correctness and left a lag: after the add
   returned, this made TWO more round trips - one for /cart.js, one for the
   rendered sections - before anything on screen moved.

   Shopify will render those sections as part of the add itself if the request
   asks for them, so cart-builds.js now puts a `sections` key in the add
   payload and the response comes back carrying the HTML. Nothing further is
   fetched, and the drawer updates in the same tick the add completes.

   /cart.js is still fetched afterwards, but only for item_count, which the
   drawer uses for its screen-reader announcement - and by then the UI has
   already moved.
   ============================================================================ */
(function () {

  if (window.__pmCartRefresh) return;
  window.__pmCartRefresh = true;

  /* Parts of a cart section that can be replaced on their own. Used when the
     drawer is open - see swap(). */
  var PIECES = [
    'cart-items-component',
    '.cart-drawer__summary',
    '.cart-drawer__footer',
    '.cart-count',
    '.cart-bubble',
    '[data-cart-count]'
  ];

  function sectionIdOf(el) {
    if (!el || !el.closest) return null;
    var wrap = el.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : null;
  }

  function sectionIds() {
    var ids = [];
    var seen = {};
    var targets = [
      document.querySelector('cart-drawer-component'),
      document.querySelector('cart-items-component'),
      document.querySelector('#header-group [id^="shopify-section-"]')
    ];
    for (var i = 0; i < targets.length; i++) {
      var t = targets[i];
      var id = (t && t.id && t.id.indexOf('shopify-section-') === 0)
        ? t.id.replace('shopify-section-', '')
        : sectionIdOf(t);
      if (id && !seen[id]) { seen[id] = true; ids.push(id); }
    }
    return ids;
  }

  /* ---- Replacing markup without closing an open drawer -------------------
     Blowing away a whole section's innerHTML destroys any <dialog> inside it,
     and a destroyed dialog is a closed dialog. That is what was slamming the
     cart shut right after it opened.

     So: if the section holds an open dialog, only the pieces that can be
     swapped independently are replaced - the item list, the summary, the count
     - and the dialog element itself is left alone. */
  function swap(id, html) {
    var host = document.getElementById('shopify-section-' + id);
    if (!host || !html) return;

    var fresh;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      fresh = doc.getElementById('shopify-section-' + id) || doc.body;
    } catch (e) {
      host.innerHTML = html;
      return;
    }

    if (!host.querySelector('dialog[open]')) {
      host.innerHTML = fresh.innerHTML;
      return;
    }

    for (var i = 0; i < PIECES.length; i++) {
      var current = host.querySelectorAll(PIECES[i]);
      var updated = fresh.querySelectorAll(PIECES[i]);
      var n = Math.min(current.length, updated.length);
      for (var j = 0; j < n; j++) {
        try { current[j].replaceWith(updated[j].cloneNode(true)); } catch (e) {}
      }
    }
  }

  function applySections(sections) {
    if (!sections) return;
    for (var id in sections) {
      if (Object.prototype.hasOwnProperty.call(sections, id)) swap(id, sections[id]);
    }
  }

  function announce(cart, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'pm-builder',
        data: {
          source: 'pm-builder',
          itemCount: cart ? cart.item_count : undefined,
          sections: sections || undefined
        }
      }
    }));
  }

  /* The fast path: markup already in hand, nothing to wait for. */
  function refreshFrom(sections) {
    applySections(sections);
    announce(null, sections);

    /* Catch up on item_count in the background - the screen is already right. */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { announce(cart, null); })
      .catch(function () {});
  }

  /* The slow path, for cart changes that did not ask for sections. */
  function refresh(cart) {
    var ids = sectionIds();

    var cartReq = cart
      ? Promise.resolve(cart)
      : fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .catch(function () { return null; });

    var sectionsReq = ids.length
      ? fetch('/?sections=' + ids.join(','), { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .catch(function () { return null; })
      : Promise.resolve(null);

    return Promise.all([cartReq, sectionsReq]).then(function (res) {
      applySections(res[1]);
      announce(res[0], res[1]);
    }).catch(function () { /* a failed refresh must never surface as a failed add */ });
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var result = origFetch.apply(this, arguments);
    try {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add.js') > -1) {
        result.then(function (response) {
          if (!response || !response.ok) return;
          /* clone() is essential - the builder reads this same response, and a
             body can only be consumed once. */
          response.clone().json().then(function (data) {
            if (data && data.sections) refreshFrom(data.sections);
            else refresh();
          }).catch(function () { refresh(); });
        }).catch(function () {});
      }
    } catch (e) {}
    return result;
  };

  document.addEventListener('cart:refresh', function () { refresh(); });

  window.__pmCart = {
    refresh: refresh,
    refreshFrom: refreshFrom,
    sectionIds: sectionIds
  };
})();
